import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Player } from "@lordicon/react";
import styles from "./WorkProcess.module.css";

import consultationIcon from "../../icons/wired-outline-981-consultation-hover-conversation-alt.json";
import planningIcon from "../../icons/wired-outline-516-square-footage-hover-layout-change (1).json";
import homeIcon from "../../icons/wired-outline-63-home-hover-3d-roll (4).json";
import projectIcon from "../../icons/wired-outline-978-project-management-hover-pinch.json";
import handshakeIcon from "../../icons/wired-outline-645-people-handshake-transaction-hover-pinch.json";

gsap.registerPlugin(ScrollTrigger);

/* Lordicon animated icon component */
const StepIcon = ({ icon, size = 34 }) => {
  const playerRef = useRef(null);
  useEffect(() => {
    const timer = setTimeout(() => playerRef.current?.playFromBeginning(), 400);
    return () => clearTimeout(timer);
  }, []);
  return (
    <Player
      ref={playerRef}
      size={size}
      icon={icon}
      colorize="#C4A882"
      onComplete={() => setTimeout(() => playerRef.current?.playFromBeginning(), 3000)}
    />
  );
};

const WHATSAPP_NUMBER = "972505864422";
const waUrl = (t) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(t)}`;

const STEPS = [
  { num: "01", title: "פגישת פרוגרמה", text: "נתחיל בשיחה מעמיקה - שאלון מסודר שנעבור עליו יחד. נכיר את השגרה שלכם, מה אתם עושים ברגע שאתם מגיעים הביתה, מה קורה בבוקר ולפני השינה. ככה נבין בדיוק מה הבית צריך לתת לכם - כי בית טוב נבנה סביב החיים שלכם, לא הפוך." },
  { num: "02", title: "תכנון והתאמה", text: "מתחילים להתאים את הבית אליכם. תקבלו 2-3 סקיצות תכנון עם הסבר מפורט - מה היתרונות, מה החסרונות, ומה הכי נכון בשבילכם. כל פונקציה במקום המחושב שלה, כל חדר עובד בשבילכם. בוחרים יחד את הכיוון שהכי מדבר אליכם." },
  { num: "03", title: "עיצוב והדמיה", text: "עוברים לשלב הכיפי - לוח השראה, בחירת צבעים, חומרים וטקסטורות. תקבלו הדמיה ריאליסטית של הבית שלא משאירה מקום לספק, עם מקום להערות ושינויים. תראו בדיוק איך הבית ייראה עוד לפני שמתחילים - בלי הפתעות." },
  { num: "04", title: "תוכניות עבודה", text: "יוצאים לדרך! מכינים תוכניות מפורטות לכל בעל מקצוע - אינסטלציה, מיזוג, חשמל, תאורה, גבס, ריצוף, נגרויות ופריסות חדרי רחצה. הקבלן מקבל הכל כתוב וברור, יודע בדיוק מה לעשות - ואתם לא צריכים להתעסק עם הפרטים." },
  { num: "05", title: "ליווי ופיקוח בשטח", text: "פיקוח עליון שמוודא שהכל נעשה לפי התוכניות. ליווי ממוקד בחנויות ריהוט, מטבחים וספקים - רק כאלה שעבדנו איתם ואנחנו סומכים עליהם. פתרונות מקצועיים לכל בעיה שצצה בשטח. אנחנו שם עד הפריט האחרון, עד שתרגישו בבית." },
];

const STEP_ICONS = [consultationIcon, planningIcon, homeIcon, projectIcon, handshakeIcon];

/* Gentle wave connector - exact original */
const WaveLine = ({ isRight, i }) => {
  const x1 = isRight ? 320 : 80;
  const x2 = isRight ? 80 : 320;
  const cp1x = x1 + (x2 - x1) * 0.3;
  const cp1y = isRight ? -10 : 90;
  const cp2x = x1 + (x2 - x1) * 0.7;
  const cp2y = isRight ? 90 : -10;
  return (
    <path
      data-zig-line
      d={`M${x1} 0 C${cp1x} ${cp1y} ${cp2x} ${cp2y} ${x2} 80`}
      stroke={`url(#zigGrad${i})`}
      strokeWidth="1"
      fill="none"
      strokeLinecap="round"
    />
  );
};

/* Vertical wave connector for mobile */
const WaveLineMobile = ({ i }) => (
  <path
    data-zig-line
    d="M20 0 C5 20 35 60 20 80"
    stroke={`url(#zigGradM${i})`}
    strokeWidth="1"
    fill="none"
    strokeLinecap="round"
  />
);

const WorkProcess = () => {
  const ref = useRef(null);
  useEffect(() => {
    const ctx = gsap.context(() => {
      /* Wave lines draw */
      ref.current.querySelectorAll("[data-zig-line]").forEach((line) => {
        const len = line.getTotalLength ? line.getTotalLength() : 0;
        if (len > 0) {
          gsap.set(line, { strokeDasharray: len, strokeDashoffset: len });
          gsap.to(line, {
            strokeDashoffset: 0, duration: 0.8, ease: "power2.out",
            scrollTrigger: { trigger: line, start: "top 85%", once: true },
          });
        }
      });
      /* Nodes pop in */
      ref.current.querySelectorAll("[data-zig-node]").forEach((node) => {
        gsap.fromTo(node, { scale: 0, opacity: 0 }, {
          scale: 1, opacity: 1, duration: 0.5, ease: "back.out(2.5)",
          scrollTrigger: { trigger: node, start: "top 82%", once: true },
        });
      });
      /* Content slides in from alternating sides */
      ref.current.querySelectorAll("[data-zig-card]").forEach((card, i) => {
        const fromX = i % 2 === 0 ? 50 : -50;
        gsap.fromTo(card, { opacity: 0, x: fromX }, {
          opacity: 1, x: 0, duration: 0.7, ease: "power3.out",
          scrollTrigger: { trigger: card, start: "top 82%", once: true },
        });
      });
      /* CTA */
      const cta = ref.current.querySelector("[data-zig-cta]");
      if (cta) {
        gsap.fromTo(cta, { opacity: 0, y: 20 }, {
          opacity: 1, y: 0, duration: 0.7, ease: "power3.out",
          scrollTrigger: { trigger: cta, start: "top 90%", once: true },
        });
      }
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={ref} className={styles.processSection} id="process">
      <div className={styles.processInner}>
        <h2 className={styles.sectionTitle}>אז איך הכל קורה..?</h2>
        <p className={styles.sectionSub}>כדי שכל משפחה תיכנס לבית שלה בראש שקט ובלי הפתעות, התהליך מסודר ב - 5 שלבים ברורים</p>

        <div className={styles.zigSteps}>
          {STEPS.map((s, i) => {
            const isRight = i % 2 === 0;
            return (
              <React.Fragment key={i}>
                <div className={`${styles.zigRow} ${isRight ? styles.zigRowRight : styles.zigRowLeft}`}>
                  <div data-zig-node className={styles.zigNode}>
                    <StepIcon icon={STEP_ICONS[i]} size={34} />
                  </div>
                  <div data-zig-card className={styles.zigCard}>
                    <h4 className={styles.zigTitle}>{s.title}</h4>
                    <p className={styles.zigText}>{s.text}</p>
                  </div>
                </div>
                {/* Desktop wave connector */}
                {i < STEPS.length - 1 && (
                  <div className={styles.zigLineWrap}>
                    <svg className={styles.zigLineSvg} viewBox="0 0 400 80" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id={`zigGrad${i}`} x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="rgba(212,190,154,0.4)" />
                          <stop offset="50%" stopColor="rgba(232,213,184,0.65)" />
                          <stop offset="100%" stopColor="rgba(212,190,154,0.4)" />
                        </linearGradient>
                      </defs>
                      <WaveLine isRight={isRight} i={i} />
                    </svg>
                  </div>
                )}
                {/* Mobile vertical wave connector */}
                {i < STEPS.length - 1 && (
                  <div className={styles.zigLineWrapMobile}>
                    <svg className={styles.zigLineSvgMobile} viewBox="0 0 40 80">
                      <defs>
                        <linearGradient id={`zigGradM${i}`} x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="rgba(212,190,154,0.35)" />
                          <stop offset="50%" stopColor="rgba(232,213,184,0.6)" />
                          <stop offset="100%" stopColor="rgba(212,190,154,0.35)" />
                        </linearGradient>
                      </defs>
                      <WaveLineMobile i={i} />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div data-zig-cta className={styles.zigCta}>
          <a href={waUrl("היי לילך, הגעתי מהאתר שלך ואשמח שנדבר")} target="_blank" rel="noopener noreferrer" className={styles.zigCtaBtn}>
            <span>לשיחת ייעוץ ללא עלות לחצו כאן</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default WorkProcess;
